import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';

// Marketing
import Marketing from './pages/marketing/Marketing';

// Subdomain helper
import { getPortalSubdomain } from './utils/subdomain';

// Import admin layout and pages
import DashboardLayout from './components/DashboardLayout';
import Dashboard from './pages/Dashboard';
import Applications from './pages/Applications';
import Companies from './pages/Companies';
import Employees from './pages/Employees';
import Vendors from './pages/Vendors';

// Import vendor pages
import VendorLoginPage from './pages/vendor/VendorLoginPage';
import VendorDashboard from './pages/vendor/VendorDashboard';

// Import admin pages
import SuperAdminLogin from './pages/admin/SuperAdminLogin';
import SuperAdminSettings from './pages/admin/SuperAdminSettings';
import MockData from './pages/admin/MockData';

// Import company admin pages
import CompanyLayout from './components/company/CompanyLayout';
import CompanyLogin from './pages/company/CompanyLogin';
import CompanyDashboard from './pages/company/CompanyDashboard';
import CompanyEmployees from './pages/company/CompanyEmployees';
import AddEmployee from './pages/company/AddEmployee';
import CompanyTiers from './pages/company/CompanyTiers';
import CompanySettings from './pages/company/CompanySettings';
import CompanyEmployeeHelp from './pages/company/CompanyEmployeeHelp';

// Import all logins page
import AllLogins from './pages/AllLogins';

/**
 * On a portal subdomain (a./c./v.loysta.com), bounce any URL whose path
 * isn't already inside the portal's section to that portal's entry.
 * On the apex domain, this is a no-op.
 */
const SubdomainGate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const portalPrefix = getPortalSubdomain();

  useEffect(() => {
    if (!portalPrefix) return;
    if (!location.pathname.startsWith(portalPrefix)) {
      navigate(portalPrefix, { replace: true });
    }
  }, [portalPrefix, location.pathname, navigate]);

  return null;
};

/**
 * Home route handler — apex domain serves the Marketing page,
 * portal subdomains redirect into their portal.
 */
const HomeRoute = () => {
  const portalPrefix = getPortalSubdomain();
  if (portalPrefix) return <Navigate to={portalPrefix} replace />;
  return <Marketing />;
};

function App() {
  return (
    <Router>
      <SubdomainGate />
      <Routes>
        {/* Apex: marketing homepage. Subdomains: redirect into portal. */}
        <Route path="/" element={<HomeRoute />} />

        {/* All Logins Hub Page (still reachable as a fallback) */}
        <Route path="/all" element={<AllLogins />} />

        {/* Admin Routes — accessible at /admin/* and at a.loysta.com */}
        <Route path="/admin/login" element={<SuperAdminLogin />} />
        <Route path="/admin/*" element={<DashboardLayout />}>
          <Route index element={<Dashboard />} />
          <Route path="applications" element={<Applications />} />
          <Route path="companies" element={<Companies />} />
          <Route path="employees" element={<Employees />} />
          <Route path="vendors" element={<Vendors />} />
          <Route path="mock-data" element={<MockData />} />
          <Route path="settings" element={<SuperAdminSettings />} />
        </Route>

        {/* Vendor Routes — accessible at /vendor/* and at v.loysta.com */}
        <Route path="/vendor" element={<Navigate to="/vendor/login" replace />} />
        <Route path="/vendor/login" element={<VendorLoginPage />} />
        <Route path="/vendor/dashboard/*" element={<VendorDashboard />} />

        {/* Employee portal lives in the Loysta Employee Flutter app, not on the web. */}

        {/* Company Admin Routes — accessible at /company/* and at c.loysta.com */}
        <Route path="/company/login" element={<CompanyLogin />} />
        <Route path="/company" element={<CompanyLayout />}>
          <Route index element={<CompanyDashboard />} />
          <Route path="employees" element={<CompanyEmployees />} />
          <Route path="add-employee" element={<AddEmployee />} />
          <Route path="tiers" element={<CompanyTiers />} />
          <Route path="settings" element={<CompanySettings />} />
          <Route path="help" element={<CompanyEmployeeHelp />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;
